/**
 * Corpus queries — §4.1.
 *
 * The corpus is a STATIC BUILD ARTEFACT: it cannot change while the tab is open, so a
 * refetch is pure waste and visited pages should stay resident. Hence
 * `staleTime: Infinity` and `gcTime: Infinity` on everything here.
 */
import { QueryClient, useQuery } from '@tanstack/react-query'
import {
  loadMeta,
  loadPageWithFonts,
  loadReciterAudio,
  loadSearchIndex,
  loadTafsirPage,
  type LoadedPage,
  type Meta,
} from '@mushaf/corpus'
import { browserFonts } from './fonts'

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: Infinity,
      gcTime: Infinity,
      retry: 1,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  },
})

export const metaQuery = {
  queryKey: ['meta'] as const,
  queryFn: ({ signal }: { signal?: AbortSignal }) => loadMeta(signal),
}

export const useMeta = () => useQuery(metaQuery)

/**
 * L1 — THE PAGE JSON AND ITS FONTS LOAD IN ONE QUERY FUNCTION, so page data is simply
 * never available early. This is the structural enforcement; nothing downstream has
 * to remember to wait.
 */
export function pageQuery(page: number, meta: Meta | undefined, withV4: boolean) {
  return {
    queryKey: ['page', page, withV4] as const,
    enabled: Boolean(meta),
    queryFn: ({ signal }: { signal?: AbortSignal }): Promise<LoadedPage> =>
      loadPageWithFonts(page, { meta: meta!, withV4, fonts: browserFonts, signal }),
  }
}

export function useMushafPage(page: number, withV4: boolean) {
  const { data: meta } = useMeta()
  return useQuery(pageQuery(page, meta, withV4))
}

export const useTafsirPage = (page: number, enabled = true) =>
  useQuery({
    queryKey: ['tafsir', page] as const,
    enabled,
    // §3.4/L20: tafsir is bundled PER MUSHAF PAGE (~23 KB), not per surah — per-surah
    // would mean a ~1 MB first click anywhere inside al-Baqarah.
    queryFn: ({ signal }) => loadTafsirPage(page, signal),
  })

export const useReciterAudio = (reciterId: string) =>
  useQuery({
    queryKey: ['audio', reciterId] as const,
    queryFn: ({ signal }) => loadReciterAudio(reciterId, signal),
  })

/** 2.8 MB, fetched when search first opens — NOT AT BOOT (§9). */
export const useSearchIndex = (enabled: boolean) =>
  useQuery({
    queryKey: ['search-index'] as const,
    enabled,
    queryFn: ({ signal }) => loadSearchIndex(signal),
  })
